import { getRaw, postRaw, ApiResponse } from 'services/api';
import { SendingDomain, TrackingDomain, AssetLink } from 'modules/mailDomains/types';

export const MAIL_DOMAINS_URL = '/settings/domains.json';
export const CREATE_SENDING_DOMAIN_URL = '/settings/sendingDomains/create';
export const DELETE_SENDING_DOMAIN_URL = '/settings/sendingDomains/delete';
export const EDIT_SENDING_DOMAIN_URL = '/settings/sendingDomains/update';

export const CREATE_TRACKING_DOMAIN_URL = '/settings/trackingDomains/create';
export const EDIT_TRACKING_DOMAIN_URL = '/settings/trackingDomains/update';
export const DELETE_TRACKING_DOMAIN_URL = '/settings/trackingDomains/delete';

export const DEEP_LINKS_URL = '/settings/deepLinks.json';
export const UPSERT_DEEP_LINK_PATH = '/mobile/upsert-app-site-association';
export const ENABLE_DEEP_LINKS_PATH = '/mobile/set-rewrite-deep-links';
export const UPSERT_ANDROID_ASSET_LINKS = '/mobile/assetlinks-update';

/**
 * Fetch the mail domains data
 */
export const getMailDomains = (): Promise<ApiResponse> => getRaw(MAIL_DOMAINS_URL);

/**
 * Create sending domain
 * @param domain
 * @param espAccountId (optional)
 */
export const createSendingDomain = (domain: string, espAccountId?: number): Promise<ApiResponse> =>
  postRaw(CREATE_SENDING_DOMAIN_URL, {
    domain,
    espAccountId,
  });

/**
 * Delete sending domain
 * @param id
 */
export const deleteSendingDomain = (id: number): Promise<ApiResponse> =>
  postRaw(DELETE_SENDING_DOMAIN_URL, {
    id,
  });

/**
 * Edit sending domain
 * @param {SendingDomain} domain
 */
export const editSendingDomain = (domain: SendingDomain): Promise<ApiResponse> =>
  postRaw(EDIT_SENDING_DOMAIN_URL, domain);

/**
 * Create tracking domain
 * @param domain
 */
export const createTrackingDomain = (domain: string): Promise<ApiResponse> =>
  postRaw(CREATE_TRACKING_DOMAIN_URL, {
    domain,
  });

/**
 * Edit tracking domain
 * @param {TrackingDomain} domain
 */
export const editTrackingDomain = (domain: TrackingDomain): Promise<ApiResponse> =>
  postRaw(EDIT_TRACKING_DOMAIN_URL, domain);

/**
 * Delete tracking domain
 * @param domain
 */
export const deleteTrackingDomain = (domain: string): Promise<ApiResponse> =>
  postRaw(DELETE_TRACKING_DOMAIN_URL, { domain });

/**
 * Fetch the deep links for an organisation
 */
export const getDeepLinks = (): Promise<ApiResponse> => getRaw(DEEP_LINKS_URL);

/**
 * Create deep links path
 * @param appDomain
 * @param domain
 * @param json
 */
export const upsertDeepLinkPath = (
  appDomain: string,
  domain: string,
  json: string,
): Promise<ApiResponse> =>
  postRaw(UPSERT_DEEP_LINK_PATH, {
    appDomain,
    domain,
    json,
  });

/**
 * Enable deep links path
 * @param appDomain
 * @param domain
 * @param enabled
 */
export const enableDeepLinkPath = (
  appDomain: string,
  domain: string | undefined,
  enabled: boolean,
): Promise<ApiResponse> =>
  postRaw(ENABLE_DEEP_LINKS_PATH, {
    appDomain,
    domain,
    enabled,
  });

/**
 * Upsert android asset links path
 * @param organizationId
 * @param domain
 * @param assetLinks
 */
export const upsertAndroidAssets = (
  organizationId: number,
  domain: string,
  assetLinks: Array<AssetLink>,
): Promise<ApiResponse> =>
  postRaw(
    `${UPSERT_ANDROID_ASSET_LINKS}?organizationId=${organizationId}&domain=${domain}`,
    assetLinks,
  );
